/**
 * Voice-related types for AI context
 *
 * Moved from contexts/admin/types/aiProvider.ts during AI context extraction.
 */

/**
 * Supported languages for voice mappings.
 * Re-exported from shared/types/audio to avoid duplicate definitions.
 */
import { Language } from '@/shared/types/audio';
export { Language };

/**
 * Voice gender
 */
export type VoiceGender = 'male' | 'female';

/**
 * Individual voice mapping entry for TTS
 * Maps language + gender to a specific voice
 */
/**
 * An admin OVERRIDE of the narration voice for one (language, gender) pair.
 * Absent pair = the worker catalogue default for the narration engine
 * (AIProviderConfigService.resolvePresetVoice). Must name a voice of the
 * narration engine; `provider` is a legacy field no longer read.
 */
export interface VoiceMappingEntry {
  language: Language;
  gender: VoiceGender;
  voiceId: string;
  voiceName: string;
  model?: string;
  previewUrl?: string;
  /** @deprecated legacy per-voice engine pin — ignored; the narration engine is tts.provider. */
  provider?: TtsProvider;
}

/**
 * TTS engines the worker can route to.
 *
 * `xai` is Grok TTS: the cheapest per character of the lot and about six times
 * faster per take than Gemini, which is what a forty-block story feels (the
 * blocks are read in order, so the per-take latency is multiplied by the
 * length of the book). It is steered by tags inside the TEXT rather than by a
 * prompt — the worker composes those (`app.services.narration.for_xai`), and
 * nothing else may, because anything that is not a known tag is read aloud.
 */
export type TtsProvider = 'elevenlabs' | 'google_ai_studio' | 'xai' | 'minimax' | 'fish' | 'fake';

/** Engines that can CLONE a voice from a short recording. */
export type CloneProvider = 'fish' | 'minimax' | 'elevenlabs';
/** Fish won the owner's blind test on his own voice (clean reference, prod curation). */
export const DEFAULT_CLONE_PROVIDER: CloneProvider = 'fish';
/**
 * LEGACY clone = an ElevenLabs-era clone (`provider` absent or 'elevenlabs'). Kept in the
 * family's library for the record (its stories still reference the voiceId) but never
 * OFFERED again: hidden from every picker, badged "legacy" in the voice library
 * (prod 2026-09-16: the fleet clones with Fish now; the old EL clones must not be chosen).
 */
export function isLegacyClonedVoice(v: { source?: string; provider?: string | null }): boolean {
  return v.source === 'cloned' && (v.provider ?? 'elevenlabs') === 'elevenlabs';
}
/**
 * Status of a voice cloning operation
 */
export type VoiceCloneStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'deleted';

/**
 * Source of a custom voice
 */
export type CustomVoiceSource = 'premade' | 'cloned';

/**
 * A custom voice entry stored in family's voice library
 */
export interface CustomVoiceEntry {
  id: string;
  voiceId: string;           // ElevenLabs provider voice ID
  name: string;              // User-given name
  source: CustomVoiceSource;
  gender?: VoiceGender;      // Reuse existing VoiceGender type
  previewUrl?: string;
  addedBy: string;           // memberId who added
  addedAt: string;           // ISO date
  status?: VoiceCloneStatus; // Only relevant for cloned voices
  language?: string;          // Language the voice was recorded in (e.g. 'ru', 'en')
  /** Engine that owns voiceId ('fish' | 'minimax' | 'elevenlabs'); absent = elevenlabs (legacy clones). */
  provider?: CloneProvider;
  /** TTS model the clone was previewed with (informational). */
  model?: string;
  /** Verbatim transcript of the curated reference (Gemini STT). */
  transcript?: string;
  /** Reference curation metrics from the worker (duration/RMS/noise/clipping, issues). */
  referenceQuality?: { duration_s: number; rms_db: number; peak_db: number; noise_floor_db: number; clipped: boolean; ok: boolean; issues: string[] };
  /** Share (0..1) of the recording script found in the transcript. */
  scriptAdherence?: number | null;
}

